import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Package, Receipt, ArrowDownToLine, CircleAlert, CircleCheck, ChevronRight, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImportHistory } from "@/components/ImportHistory";
import Billing from "@/pages/Billing";
import { OrderBookingData } from "@/components/OrderBookingData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SOURCE_LABELS = {
  milestone_api: ["Milestone — Product API", "bg-indigo-100 text-indigo-800"],
  milestone_billing_api: ["Milestone — Billing API", "bg-emerald-100 text-emerald-800"],
  milestone_billing_csv: ["Milestone — Daily CSV", "bg-teal-100 text-teal-800"],
  billing_excel_upload: ["Billing Excel upload", "bg-slate-100 text-slate-600"],
  billing_initial_seed: ["Billing initial seed", "bg-slate-100 text-slate-500"],
  initial_seed: ["Catalog initial seed", "bg-slate-100 text-slate-500"],
  excel_upload: ["ERP codes Excel upload", "bg-slate-100 text-slate-600"],
  dtp_upload: ["DTP price import", "bg-amber-100 text-amber-800"],
};

const STATUS_BADGE = {
  new: ["NEW", "bg-emerald-100 text-emerald-700"],
  updated: ["UPDATED", "bg-sky-100 text-sky-700"],
  unchanged: ["UNCHANGED", "bg-slate-100 text-slate-500"],
};

const inr = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const STALE_MS = 24 * 60 * 60 * 1000;

function LastPushCard({ title, icon: Icon, log, testId }) {
  const stale = log && Date.now() - new Date(log.at).getTime() > STALE_MS;
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h3 className="font-heading font-semibold text-sm">{title}</h3>
      </div>
      {!log ? (
        <p className="text-sm text-slate-400 flex items-center gap-1.5"><CircleAlert className="h-4 w-4 text-amber-500" /> No push received yet</p>
      ) : (
        <div className="space-y-1">
          <p className={`text-sm font-medium flex items-center gap-1.5 ${stale ? "text-amber-700" : "text-emerald-700"}`}>
            {stale ? <CircleAlert className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            {timeAgo(log.at)}{stale ? " — over 24h ago" : ""}
          </p>
          <p className="text-xs text-slate-500 font-mono">{log.received} received · {log.passed ?? log.received} passed · {log.failed || 0} failed</p>
          <p className="text-[11px] text-slate-400">{new Date(log.at).toLocaleString("en-IN")}</p>
        </div>
      )}
    </Card>
  );
}

function SyncDetailDialog({ logId, onOpenChange }) {
  const { data: doc, isLoading } = useQuery({
    queryKey: ["erp-sync-detail", logId],
    queryFn: async () => (await api.get(`/integrations/erp/sync-log/${logId}`)).data,
    enabled: !!logId,
  });
  const [label, cls] = doc ? (SOURCE_LABELS[doc.source] || [doc.source, "bg-slate-100 text-slate-600"]) : ["", ""];
  const lines = doc?.lines || [];
  const items = doc?.items || [];
  return (
    <Dialog open={!!logId} onOpenChange={(v) => { if (!v) onOpenChange(null); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sync-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Push details
            {doc && <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {doc ? <>{new Date(doc.at).toLocaleString("en-IN")} · {doc.received} received · {doc.created ?? 0} new · {doc.updated ?? 0} updated
              {doc.taxable_total ? <> · <b className="text-slate-700">{inr(doc.taxable_total)} taxable</b></> : null}
              {doc.by ? ` · by ${doc.by}` : ""}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : lines.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Invoice", "Date", "Customer", "Project", "Taxable", "Region", "Status"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const [sl, sc] = STATUS_BADGE[l.status] || [l.status, "bg-slate-100 text-slate-500"];
                  return (
                    <TableRow key={i} data-testid={`sync-detail-line-${i}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{l.invoice_no}{l.line_no ? <span className="text-slate-300">/{l.line_no}</span> : ""}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{l.invoice_date}</TableCell>
                      <TableCell className="text-xs max-w-[160px] truncate">{l.customer_name}</TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate text-slate-500">{l.project_name}</TableCell>
                      <TableCell className="text-right font-mono text-xs font-semibold">{inr(l.taxable_value)}</TableCell>
                      <TableCell className="text-xs">{l.region}</TableCell>
                      <TableCell><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${sc}`}>{sl}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {doc.received > lines.length && <p className="text-[11px] text-slate-400 mt-2">Showing first {lines.length} of {doc.received} rows.</p>}
          </div>
        ) : items.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Item Code", "Status", "Error"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((it, i) => (
                  <TableRow key={i} data-testid={`sync-detail-item-${i}`}>
                    <TableCell className="font-mono text-xs">{it.itemCode || "—"}</TableCell>
                    <TableCell><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${it.status === "Pass" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{it.status}</span></TableCell>
                    <TableCell className="text-xs text-red-600">{it.error || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-10" data-testid="sync-detail-empty">
            Record-level details weren't captured for this push — detail recording was added in Aug 2026. Newer pushes will show every line here.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ErpSyncLog() {
  const [detailId, setDetailId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingRecv, setUploadingRecv] = useState(false);
  const fileRef = useRef(null);
  const recvRef = useRef(null);
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["erp-sync-log"],
    queryFn: async () => (await api.get("/integrations/erp/sync-log")).data,
    refetchInterval: 30000,
  });

  const uploadWorkbook = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/billing/upload-invoiced-workbook", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      const s = r.data;
      toast.success(`Imported ${s.invoices} invoices (${s.lines} lines, ${s.value_cr} Cr)${s.targets ? ` + ${s.targets} FY targets` : ""}`);
      refetch();
      qc.invalidateQueries({ queryKey: ["invoiced-analytics"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadReceivables = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingRecv(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/billing/upload-receivables", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      const s = r.data;
      toast.success(`Receivables updated — ${s.invoices} invoices, ${(s.total_outstanding / 1e7).toFixed(2)} Cr outstanding (${s.open} open, ${s.partial} partial). Commercials on the Sales Dashboard are now live.`, { duration: 8000 });
      refetch();
      qc.invalidateQueries({ queryKey: ["sales-bi"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingRecv(false);
    }
  };

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const logs = data?.logs || [];

  return (
    <div>
      <PageHeader title="ERP Sync Log" subtitle="Inbound Milestone ERP pushes plus the full billing & payments workspace.">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={uploadWorkbook} data-testid="invoiced-workbook-input" />
        <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-invoiced-workbook-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Invoiced Workbook
        </Button>
        <input ref={recvRef} type="file" accept=".xlsx" className="hidden" onChange={uploadReceivables} data-testid="receivables-input" />
        <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => recvRef.current?.click()} disabled={uploadingRecv} data-testid="upload-receivables-btn">
          {uploadingRecv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Receivables (SalesData)
        </Button>
        <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => refetch()} disabled={isFetching} data-testid="sync-log-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      <Tabs defaultValue="log">
        <TabsList className="mb-4">
          <TabsTrigger value="log" data-testid="erp-tab-log">Sync Log</TabsTrigger>
          <TabsTrigger value="billing" data-testid="erp-tab-billing">Billing & Payments</TabsTrigger>
          <TabsTrigger value="order-booking" data-testid="erp-tab-order-booking">Daily Order Booking Data</TabsTrigger>
          <TabsTrigger value="import-history" data-testid="erp-tab-import-history">Import History</TabsTrigger>
        </TabsList>
        <TabsContent value="import-history">
          <ImportHistory />
        </TabsContent>
        <TabsContent value="billing">
          <Billing embedded />
        </TabsContent>
        <TabsContent value="order-booking">
          <OrderBookingData />
        </TabsContent>
        <TabsContent value="log">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <LastPushCard title="Last Product Push" icon={Package} log={data?.last_products} testId="last-product-push-card" />
        <LastPushCard title="Last Billing Push" icon={Receipt} log={data?.last_billing} testId="last-billing-push-card" />
        <Card className="p-5" data-testid="total-syncs-card">
          <div className="flex items-center gap-2 mb-2">
            <ArrowDownToLine className="h-4 w-4 text-slate-500" />
            <h3 className="font-heading font-semibold text-sm">Total Syncs</h3>
          </div>
          <p className="font-mono text-2xl font-bold">{data?.total ?? 0}</p>
          <p className="text-[11px] text-slate-400">all sources, since go-live</p>
        </Card>
      </div>

      <Card className="overflow-hidden" data-testid="sync-log-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["When", "Source", "Received", "Passed", "Failed", "Created", "Updated", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {logs.map((l) => {
                const [label, cls] = SOURCE_LABELS[l.source] || [l.source, "bg-slate-100 text-slate-600"];
                return (
                  <TableRow key={l.id} onClick={() => setDetailId(l.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`sync-log-row-${l.id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{new Date(l.at).toLocaleString("en-IN")}<span className="text-slate-400 text-xs block">{timeAgo(l.at)}</span></TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span></TableCell>
                    <TableCell className="font-mono">{l.received ?? "—"}</TableCell>
                    <TableCell className="font-mono text-emerald-700">{l.passed ?? l.received ?? "—"}</TableCell>
                    <TableCell className={`font-mono ${l.failed ? "text-red-600 font-bold" : "text-slate-400"}`}>{l.failed || 0}</TableCell>
                    <TableCell className="font-mono">{l.created ?? "—"}</TableCell>
                    <TableCell className="font-mono">{l.updated ?? "—"}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-slate-300" /></TableCell>
                  </TableRow>
                );
              })}
              {logs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">No syncs yet — waiting for the first push from Milestone.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
        </TabsContent>
      </Tabs>

      <SyncDetailDialog logId={detailId} onOpenChange={setDetailId} />
    </div>
  );
}
